/**
 * The reviewer's screen (APP_SPEC box 6, PRD-159). Every pending
 * attestation is a card that shows the citation FIRST -- the section
 * and the book the question stands on -- then the rule's summary, the
 * prompt as the rule asks it, and the subject: the element by its
 * authored name, the zone, or the project for a system-scope question.
 * The answer is a verdict from the wire's three and how the person
 * knows; the wire demands both, so the button holds until both are
 * given. Below, every finding of the latest inspection sits beside the
 * citation its own inspection recorded. Strata only: no stylesheet.
 */
import { useState } from 'react';
import {
  ActionButton,
  Choice,
  Field,
  Grid,
  Notice,
  Panel,
  Stack,
  Text,
  Title,
  type ChoiceOption,
  type GridColumn,
} from '../../../strata';
import {
  useAnswer,
  useFindings,
  usePending,
  type AttestationVerdict,
  type FindingView,
  type PendingAttestation,
} from '../queries';

/** The three answers the wire takes, each with the word a person reads; the Record keeps the list whole. */
const VERDICT_LABELS: Record<AttestationVerdict, string> = {
  complies: 'Complies',
  breach: 'Breach',
  not_applicable: 'Not applicable',
};

const VERDICTS: readonly ChoiceOption<AttestationVerdict>[] = (
  Object.entries(VERDICT_LABELS) as [AttestationVerdict, string][]
).map(([key, label]) => ({ key, label }));

function isVerdict(key: unknown): key is AttestationVerdict {
  return typeof key === 'string' && Object.hasOwn(VERDICT_LABELS, key);
}

/** The question's subject in a person's words: the element's authored name, else its id; the zone; the project. */
export function subjectOf(question: PendingAttestation): string {
  if (question.element_name) return question.element_name;
  const { element, zone } = question.dimensions;
  if (typeof element === 'string') return element;
  if (typeof zone === 'string') return `Zone ${zone}`;
  return 'This project';
}

/** One subject key as one string, for React keys and grid rows. */
const subjectKey = (ruleName: string, dimensions: Record<string, unknown>) =>
  `${ruleName}:${JSON.stringify(Object.entries(dimensions).sort(([a], [b]) => a.localeCompare(b)))}`;

/** The citation as every card and row prints it: the section, then the book. */
function Citation({ section, code }: { section: string; code: string }) {
  return (
    <Stack direction="row" gap="sm" align="baseline" wrap>
      <Text size="control" weight={600}>
        {section}
      </Text>
      <Text size="caption" tone="secondary">
        {code}
      </Text>
    </Stack>
  );
}

function QuestionCard({ projectId, question }: { projectId: string; question: PendingAttestation }) {
  const [verdict, setVerdict] = useState<AttestationVerdict | null>(null);
  const [provenance, setProvenance] = useState('');
  const answer = useAnswer(projectId);
  const complete = verdict !== null && provenance.trim().length > 0;
  const heading = `${question.section} ${question.code}`;

  return (
    <Panel role="article" ariaLabel={heading}>
      <Stack direction="column" gap="sm">
        <Citation section={question.section} code={question.code} />
        <Text size="caption" tone="secondary" as="p">
          {question.summary}
        </Text>
        <Text as="p">{question.prompt}</Text>
        <Text size="caption" tone="muted">
          Subject: {subjectOf(question)}
        </Text>
        <Stack direction="row" gap="md" align="flex-end" wrap>
          <Choice
            label="Verdict"
            look={{ kind: 'field', size: 'sm', labelHidden: false }}
            options={VERDICTS}
            placeholder="Choose"
            selectedKey={verdict}
            onSelectionChange={(key) => setVerdict(isVerdict(key) ? key : null)}
          />
          <Field
            label="How you know"
            size="sm"
            isRequired
            placeholder="the walkthrough, the photo, the drawing sheet"
            value={provenance}
            onChange={setProvenance}
          />
          <ActionButton
            variant="brand"
            size="sm"
            isDisabled={!complete || answer.isPending}
            isPending={answer.isPending}
            onPress={() => {
              if (verdict === null) return;
              answer.mutate({
                rule_name: question.rule_name,
                dimensions: question.dimensions,
                verdict,
                provenance: provenance.trim(),
              });
            }}
          >
            Answer
          </ActionButton>
        </Stack>
        {answer.isError && <Notice kind="error" title="The answer was not recorded." />}
      </Stack>
    </Panel>
  );
}

const FINDING_COLUMNS: readonly GridColumn<FindingView>[] = [
  {
    id: 'citation',
    header: 'Citation',
    width: 180,
    cell: ({ row }) => <Citation section={row.original.section} code={row.original.code} />,
  },
  {
    id: 'summary',
    header: 'Summary',
    cell: ({ row }) => <Text size="control">{row.original.summary}</Text>,
  },
  {
    id: 'provenance',
    header: 'Provenance',
    width: 200,
    cell: ({ row }) => (
      <Text size="caption" tone="secondary">
        {row.original.provenance}
      </Text>
    ),
  },
  {
    id: 'ruling',
    header: 'Ruling',
    width: 110,
    cell: ({ row }) => <Text size="caption">{row.original.ruling}</Text>,
  },
];

export function ReviewPanel({ projectId }: { projectId: string }) {
  const pending = usePending(projectId);
  const findings = useFindings(projectId);

  return (
    <Stack direction="column" gap="lg">
      <Stack direction="column" gap="md">
        <Title level={3}>Pending attestations</Title>
        {pending.isError && <Notice kind="error" title="Pending attestations failed to load." />}
        {pending.isLoading && <Text tone="secondary">Loading pending attestations…</Text>}
        {pending.data && pending.data.length === 0 && (
          <Text tone="secondary">Every attestation this project owes has been answered.</Text>
        )}
        {pending.data?.map((question) => (
          <QuestionCard
            key={subjectKey(question.rule_name, question.dimensions)}
            projectId={projectId}
            question={question}
          />
        ))}
      </Stack>
      <Stack direction="column" gap="md">
        <Title level={3}>Findings</Title>
        {findings.isError && <Notice kind="error" title="Findings failed to load." />}
        {findings.isLoading && <Text tone="secondary">Loading findings…</Text>}
        {findings.data && (
          <Grid
            ariaLabel="Findings"
            rows={findings.data}
            columns={FINDING_COLUMNS}
            rowKey={(finding) => subjectKey(finding.rule_name, finding.dimensions)}
            empty="No inspection has run on this project yet."
          />
        )}
      </Stack>
    </Stack>
  );
}
